import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, LayoutDashboard, Flag, CheckSquare, Timer, Package, Settings } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import ProjectMilestones from './projects/ProjectMilestones';
import ProjectTasks from './projects/ProjectTasks';
import ProjectTimesheets from './projects/ProjectTimesheets';
import ProjectDeliverables from './projects/ProjectDeliverables';

const ProjectDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [project, setProject] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');


    useEffect(() => {
        const fetchProject = async () => {
            try {
                const res = await apiFetch(`/projects/${id}`);
                if (res.ok) setProject(await res.json());
                else navigate('/admin/projects');
            } catch (err) {
                console.error(err);
                navigate('/admin/projects');
            } finally {
                setLoading(false);
            }
        };
        fetchProject();
    }, [id, navigate]);

    if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="animate-spin text-blue-500" size={48} /></div>;
    if (!project) return null;

    const tabs = [
        { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={18} /> },
        { id: 'milestones', label: 'Milestones', icon: <Flag size={18} /> },
        { id: 'tasks', label: 'Tasks', icon: <CheckSquare size={18} /> },
        { id: 'timesheets', label: 'Timesheets', icon: <Timer size={18} /> },
        { id: 'deliverables', label: 'Deliverables', icon: <Package size={18} /> },
        { id: 'settings', label: 'Settings', icon: <Settings size={18} /> },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate('/admin/projects')} className="p-2 hover:bg-gray-100 rounded-full transition">
                    <ArrowLeft size={24} className="text-gray-600" />
                </button>
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{project.project_name}</h2>
                    <p className="text-gray-500 font-medium">{project.account_name}</p>
                </div>
                <div className="ml-auto flex gap-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider">{project.stage}</span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold uppercase tracking-wider">{project.opportunity_type}</span>
                </div>
            </div>

            <div className="flex border-b border-gray-100 gap-8">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 py-4 border-b-2 transition-all font-semibold text-sm ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
                            }`}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="mt-6">
                {activeTab === 'overview' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-8">
                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                <h3 className="text-xl font-bold mb-6">Description</h3>
                                <p className="text-gray-600 leading-relaxed">{project.description || 'No description provided.'}</p>
                            </div>
                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                <h3 className="text-xl font-bold mb-6">Technologies</h3>
                                <div className="flex flex-wrap gap-2">
                                    {project.technologies?.map((tech: string) => (
                                        <span key={tech} className="px-4 py-2 bg-gray-50 text-gray-700 rounded-xl font-medium border border-gray-100">{tech}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="space-y-8">
                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                <h3 className="text-xl font-bold mb-6">Timeline</h3>
                                <div className="space-y-4">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Start Date</span>
                                        <span className="font-bold text-gray-800">{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'TBD'}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-500">Target Delivery</span>
                                        <span className="font-bold text-gray-800">{project.expected_delivery_date ? new Date(project.expected_delivery_date).toLocaleDateString() : 'TBD'}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                <h3 className="text-xl font-bold mb-6">Team Size</h3>
                                <p className="text-3xl font-bold text-blue-600">{project.resource_count} <span className="text-sm text-gray-400">Resources</span></p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'milestones' && <ProjectMilestones projectId={id!} />}
                {activeTab === 'tasks' && <ProjectTasks projectId={id!} />}
                {activeTab === 'timesheets' && <ProjectTimesheets projectId={id!} />}
                {activeTab === 'deliverables' && <ProjectDeliverables projectId={id!} />}
                {activeTab === 'settings' && <div className="p-8 text-center text-gray-500">Advanced settings coming soon...</div>}
            </div>
        </div>
    );
};

export default ProjectDetail;
